/**
 * Persists authorisation review outcomes: analytics dump API and Postgres updates.
 *
 * @module authorisationDetails/utils/saveReviewPersistence
 */
import { postAuthLetterReviewDump } from "../../../api/postCall/authLetterReviewDump";
import { JCodeDetails } from "../../../data-model/medicalPaOrdersModel";
import { buildAuthLetterReviewDumpPayload } from "./buildReviewPayloads";
import {
  BasicInfoFieldState,
  ProcedureFieldState,
  type ApprovalStatus,
} from "./constants";

/**
 * Posts the review payload to the analytics/ML dump API.
 *
 * @param orderId - PA order id
 * @param patientId - Patient identifier (MRN) required by auth-letter review dump API
 * @param orgId - Organization id
 * @param primaryPayerNameFallback - Payer label when basic-info state omits primary insurance
 * @param mergedJCodes - J-codes after UI merge
 * @param basicInfoFieldState - Optional shared fields included in the dump
 * @param procedureStatuses - Optional per–J-code approval metadata
 * @returns Resolves when the HTTP call completes
 */
export async function submitReviewDump(
  orderId: string,
  patientId: string,
  orgId: string,
  primaryPayerNameFallback: string,
  mergedJCodes: JCodeDetails[],
  basicInfoFieldState?: BasicInfoFieldState | null,
  basicInfoStatus?: ApprovalStatus,
  diagnosisDetailStatus?: ApprovalStatus,
  procedureStatuses?: Record<string, ApprovalStatus>,
  procedureFieldStateMap?: Record<string, ProcedureFieldState> | null,
): Promise<void> {
  const dumpPayload = buildAuthLetterReviewDumpPayload(
    orderId,
    patientId,
    orgId,
    primaryPayerNameFallback,
    mergedJCodes,
    basicInfoFieldState,
    basicInfoStatus,
    diagnosisDetailStatus,
    procedureStatuses,
    procedureFieldStateMap,
  );
  await postAuthLetterReviewDump(dumpPayload);
}
